import {
  Building2,
  Calculator,
  FileText,
  Home,
  Settings,
  Users,
  ClipboardList,
  Handshake,
  BarChart3,
  Wallet,
  Bot,
  UserPlus,
  UserRoundCheck,
  User,
  MessageSquarePlus,
  LayoutDashboard,
  FolderArchive,
  Target,
  Activity,
  BadgeDollarSign,
  Megaphone,
  TrendingUp,
  SlidersHorizontal,
  Mail
} from 'lucide-react';

export const MENU_FEATURE_FLAGS = {
  // Not one database flag: Online Leads opens when either lead source is on
  // (useOnlineLeadsGate: website messages or the rate funnel).
  onlineLeads: 'online_leads',
  pickupRepriceOpportunities: 'pickup_reprice_opportunities_v1',
  marketingStudio: 'marketing_studio_v1',
  myEmailNotifications: 'my_email_notifications_v1'
} as const;

export type MenuFeatureFlag = typeof MENU_FEATURE_FLAGS[keyof typeof MENU_FEATURE_FLAGS];

/** The sidebar's Online Leads item opens the Artemis tab on this lane. */
export const ARTEMIS_ONLINE_LEADS_PATH = '/internal/artemis?view=online-leads';

export interface MenuItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  badge?: string;
  permissions?: string[];
  featureFlag?: MenuFeatureFlag;
  hideWhenFeatureEnabled?: MenuFeatureFlag;
  subItems?: {
    id: string;
    title: string;
    href: string;
    permissions?: string[];
    featureFlag?: MenuFeatureFlag;
    hideWhenFeatureEnabled?: MenuFeatureFlag;
  }[];
}

export interface MenuSection {
  title?: string;
  items: MenuItem[];
}

export const menuSections: MenuSection[] = [
  {
    items: [
      {
        id: "home",
        title: "Home",
        icon: Home,
        href: "/home",
        permissions: ['view_dashboard']
      },
      {
        id: "system-status",
        title: "Wiseboard",
        icon: Activity,
        href: "/system-status",
        permissions: ['Admin']
      },
      {
        id: "company-settings",
        title: "Company Settings",
        icon: Building2,
        href: "/company-settings",
        permissions: ['access_company_settings'],
        subItems: [
          { id: "company-profile", title: "Company Profile", href: "/company-settings/profile", permissions: ['edit_company_profile'] },
          { id: "branch-offices", title: "Branch Offices", href: "/company-settings/branches", permissions: ['manage_branch_offices'] },
          { id: "manage-users", title: "Manage Users", href: "/company-settings/users", permissions: ['view_users'] },
          { id: "role-permissions", title: "Role Permissions", href: "/company-settings/permissions", permissions: ['manage_role_permissions'] },
          { id: "closing-cost-admin", title: "Closing Cost Admin", href: "/company-settings/closing-costs", permissions: ['manage_closing_costs'] },
          { id: "title-escrow-companies", title: "Title & Escrow Companies", href: "/company-settings/title-escrow", permissions: ['manage_closing_costs'] },
          { id: "condition-templates", title: "Condition Automations", href: "/company-settings/condition-templates", permissions: ['Admin'] },
          { id: "user-compensation", title: "User Compensation", href: "/company-settings/user-compensation", permissions: ['manage_compensation_plans'] },
          { id: "google-business-profile", title: "Google Business Profile", href: "/company-settings/google-business-profile", permissions: ['Admin'] },
          { id: "messages", title: "Messages", href: "/company-settings/messages", permissions: ['manage_communications'] },
          { id: "field-discovery", title: "Field Discovery", href: "/company-settings/field-discovery", permissions: ['Admin'] },
          { id: "email-notifications", title: "Email Notifications", href: "/company-settings/email-notifications", permissions: ['Admin', 'Partner'] },
          { id: "ob-builder", title: "OB Builder", href: "/company-settings/ob-builder", permissions: ['Admin'] }
        ]
      },
      {
        id: "market-watch",
        title: "Market Watch",
        icon: TrendingUp,
        permissions: ['view_market_dashboard']
      },
      {
        id: "talent-pipeline",
        title: "Talent Pipeline",
        icon: UserPlus,
        permissions: ['view_recruiting'],
        subItems: [
          // Prospects transition into Recruiting; both share view_recruiting.
          { id: "lo-prospects", title: "Prospects", href: "/internal?section=lo-prospects", permissions: ['view_recruiting'] },
          { id: "recruiting", title: "Recruiting", href: "/internal?section=recruiting", permissions: ['view_recruiting'] }
        ]
      },
      {
        id: "batcave",
        title: "Bat Cave",
        icon: FolderArchive,
        href: "/batcave-admin",
        permissions: ['view_batcave', 'manage_closing_costs']
      }
    ]
  },
  {
    items: [
      {
        id: "pipelines",
        title: "Pipelines",
        icon: LayoutDashboard,
        permissions: ['view_dashboard'],
        subItems: [
          { id: "disclosures", title: "Disclosures", href: "/internal?section=disclosures", permissions: ['view_dashboard'] }
        ]
      },
      {
        id: "secondary",
        title: "Secondary",
        icon: BadgeDollarSign,
        permissions: ['Admin', 'view_pickup'],
        featureFlag: MENU_FEATURE_FLAGS.pickupRepriceOpportunities,
        subItems: [
          {
            id: "pickup-report",
            title: "Pick Up Analysis",
            href: "/internal/secondary/pickup-analysis",
            permissions: ['Admin', 'view_pickup'],
            featureFlag: MENU_FEATURE_FLAGS.pickupRepriceOpportunities
          },
          {
            id: "pickup-pipeline",
            title: "Pick Up Pipeline",
            href: "/internal/secondary/pickup-pipeline",
            permissions: ['Admin', 'view_pickup'],
            featureFlag: MENU_FEATURE_FLAGS.pickupRepriceOpportunities
          }
        ]
      },
      {
        id: "loan-center",
        title: "Loan Center",
        icon: ClipboardList,
        href: "/loan-center",
        permissions: ['view_live_loans', 'generate_reports'],
        subItems: [
          { id: "loan-center", title: "Loans", href: "/loan-center", permissions: ['view_live_loans'] },
          { id: "loan-data-exports", title: "Data Exports", href: "/loan-data-exports", permissions: ['generate_reports'] }
        ]
      },
      {
        id: "income-calculation",
        title: "Income Calculation",
        icon: Calculator,
        href: "/income-calculation",
        permissions: ['view_live_loans']
      },
      {
        id: "borrowers",
        title: "Borrowers & Leads",
        icon: Users,
        href: "/borrowers",
        permissions: ['view_borrowers']
      },
      {
        id: "referral-partners",
        title: "Co-Marketing Partners",
        icon: Handshake,
        href: "/partners",
        permissions: ['view_partners']
      },
      {
        id: "manage-investors",
        title: "Manage Investors",
        icon: Wallet,
        href: "/company-settings/investors",
        permissions: []
      }
    ]
  },
  {
    title: "Tools",
    items: [
      {
        id: "marketing",
        title: "Marketing Studio",
        icon: Megaphone,
        href: "/marketing",
        permissions: ['manage_marketing_studio', 'use_marketing_studio'],
        featureFlag: MENU_FEATURE_FLAGS.marketingStudio
      },
      {
        id: "artemis",
        title: "Artemis",
        icon: Target,
        href: "/internal/artemis",
        permissions: []
      },
      {
        id: "artemis-online-leads",
        title: "Online Leads",
        icon: UserRoundCheck,
        href: "/internal/artemis/online-leads",
        permissions: [],
        featureFlag: MENU_FEATURE_FLAGS.onlineLeads
      },
      {
        id: "pre-approval",
        title: "Pre-Approval",
        icon: FileText,
        href: "/tools/pre-approval",
        permissions: ['create_pre_approval']
      },
      {
        // User-side email activity on the user's own loans. `?section=` is the form that
        // actually navigates (the `/tools/...` hrefs above have no route of their own).
        id: "my-email-notifications",
        title: "My Email Notifications",
        icon: Mail,
        href: "/internal?section=my-email-notifications",
        permissions: ['view_live_loans'],
        featureFlag: MENU_FEATURE_FLAGS.myEmailNotifications
      },
      {
        id: "ai-support",
        title: "AI Support",
        icon: Bot,
        href: "/tools/ai-support",
        permissions: ['view_ai_support']
      },
      {
        id: "ai-settings",
        title: "AI Settings",
        icon: SlidersHorizontal,
        href: "/tools/ai-settings",
        permissions: ['Admin']
      },
      {
        id: "accounting",
        title: "Accounting",
        icon: User,
        permissions: ['manage_compensation_plans', 'view_own_servicing', 'view_own_commissions'],
        subItems: [
          { id: "broker-recon", title: "Broker Recon", href: "/accounting/broker-recon", permissions: ['manage_compensation_plans'] },
          { id: "banker-recon", title: "Banker Recon", href: "/accounting/banker-recon", permissions: ['manage_compensation_plans'] },
          { id: "my-commissions", title: "My Commissions", href: "/accounting/my-commissions", permissions: ['manage_compensation_plans', 'view_own_commissions'] },
          { id: "servicing", title: "Servicing", href: "/accounting/servicing", permissions: ['manage_compensation_plans', 'view_own_servicing'] }
        ]
      },
      {
        id: "feedback",
        title: "Feedback",
        icon: MessageSquarePlus,
        href: "/feedback",
        permissions: [] // Available to all users
      },
      {
        id: "settings",
        title: "Settings",
        icon: Settings,
        href: "/settings",
        permissions: [], // Available to all users
        subItems: [
          { id: "my-profile", title: "My Profile", href: "/settings/my-profile", permissions: ['edit_own_profile'] },
          { id: "company-profile-settings", title: "Company Profile", href: "/settings/company-profile", permissions: ['edit_company_profile'] },
          { id: "pre-approval-templates", title: "Pre-Approval Templates", href: "/settings/pre-approval-templates", permissions: ['manage_pre_approval_templates'] }
        ]
      }
    ]
  },
  {
    title: "Company Reports",
    items: [
      {
        id: "company-reports",
        title: "Company Reports",
        icon: BarChart3,
        href: "/company-reports",
        permissions: ['view_reports', 'generate_reports', 'view_lo_scoreboard'],
        subItems: [
          { id: "pipeline-performance-report", title: "Pipeline Performance", href: "/company-reports/pipeline-performance", permissions: ['view_reports', 'generate_reports'] },
          { id: "pipeline-status-report", title: "Pipeline Status", href: "/company-reports/pipeline-status", permissions: ['view_reports', 'generate_reports'] },
          { id: "funded-loan-report", title: "Funded Loans", href: "/company-reports/funded-loans", permissions: ['view_reports', 'generate_reports'] },
          { id: "lo-scoreboard-report", title: "LO Scoreboard", href: "/company-reports/lo-scoreboard", permissions: ['generate_reports', 'view_lo_scoreboard'] },
          {
            id: "pickup-report",
            title: "Pick Up",
            href: "/company-reports/pickup",
            permissions: ['generate_reports'],
            hideWhenFeatureEnabled: MENU_FEATURE_FLAGS.pickupRepriceOpportunities
          },
          { id: "company-revenue-report", title: "Company Revenue", href: "/company-reports/company-revenue", permissions: ['generate_reports'] },
          { id: "fraud-alerts-report", title: "Fraud Alerts", href: "/company-reports/fraud-alerts", permissions: ['view_reports', 'generate_reports'] },
          { id: "appraisal-delivery-report", title: "Appraisal Processes", href: "/company-reports/appraisal-delivery", permissions: ['view_reports', 'generate_reports'] }
        ]
      }
    ]
  }
];
